import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Action, Modal, styles, Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { L10N } from './BookingTerms.l10n.js';
import * as style from './BookingTerms.module.css';
import { TYPE } from '../../../Core.constants.js';
import { Field } from '../../Booking/components';
import { EVENT, getPrivacyPolicy, METRICS_EVENTS } from '../../helpers';
import { parseHtml } from '../../helpers/parseHtml';

const BookingTerms = ({
  dataSource: { cancellationPolicy, otherConditions, paymentsConditions } = {},
  small,
  trackBookingInteraction = false,
  ...others
}) => {
  const { translate } = useLocale();
  const { value: { hotel = {}, id, language, locale, type } = {} } = useStore();

  const [modal, setModal] = useState(false);

  const handleModal = () => {
    setModal(true);
    if (trackBookingInteraction) {
      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.BOOKING_INTERACTION,
        action: 'cancellation_policy_view',
      });
    }
  };

  const fieldProps = { inline: false, small };
  const { name: privacyName, url: privacyUrl } = getPrivacyPolicy({
    chainId: id,
    features: hotel.features,
    genericName: translate(L10N.LABEL_GENERIC_NAME),
    hotelId: hotel.id,
    isChain: type === TYPE.CHAIN,
    language,
    locale,
  });

  return (
    <>
      <View {...others} className={styles(style.box, style.others, others.className)}>
        {paymentsConditions && (
          <Field
            {...fieldProps}
            label={translate(L10N.LABEL_PAYMENT_TERMS)}
            value={parseHtml(paymentsConditions, { small })}
          />
        )}

        {cancellationPolicy && (
          <Field {...fieldProps} label={translate(L10N.LABEL_CANCELLATION_POLICY)} value={cancellationPolicy.summary}>
            {cancellationPolicy?.detail && (
              <Action small={small} underline onPress={handleModal} className={style.noPrint}>
                {translate(L10N.ACTION_MORE_INFO)}
              </Action>
            )}
          </Field>
        )}

        {privacyUrl && (
          <Field
            {...fieldProps}
            label={translate(L10N.LABEL_PRIVACY_POLICY)}
            value={translate(L10N.LABEL_PRIVACY_POLICY_DESCRIPTION, {
              name: privacyName,
              link: (
                <Action
                  href={privacyUrl}
                  target="_blank"
                  small={small}
                  underline
                  className={styles(style.linkPrivacy, style.noPrint)}
                >
                  {translate(L10N.ACTION_MORE_INFO)}
                </Action>
              ),
            })}
          />
        )}
        {otherConditions && (
          <Field
            {...fieldProps}
            label={translate(L10N.LABEL_OTHERS_TERMS)}
            value={parseHtml(otherConditions, { small })}
          />
        )}
      </View>

      {cancellationPolicy?.detail && (
        <Modal title={translate(L10N.LABEL_CANCELLATION_POLICY)} visible={modal} onClose={() => setModal(false)}>
          <Text className={style.modal}>{cancellationPolicy.detail}</Text>
        </Modal>
      )}
    </>
  );
};

BookingTerms.displayName = 'Mirai:Core:BookingTerms';

BookingTerms.propTypes = {
  dataSource: PropTypes.shape({
    cancellationPolicy: PropTypes.shape({
      detail: PropTypes.string,
      summary: PropTypes.string,
    }),
    payment: PropTypes.shape({}),
    paymentsConditions: PropTypes.string,
  }),
  small: PropTypes.bool,
  trackBookingInteraction: PropTypes.bool,
};

export { BookingTerms };
